function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { forwardRef, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { white, gray400, gray500, gray600, gray800, primary100, primary600, focusShadow } from '../../theme/colors';
import { size2, size4, size8, size12, size14, size16, size20, size24, size32, size40, size48 } from '../../theme/scale';
import { duration150, easeIn } from '../../theme/animation';
import { elevationThree } from '../../theme/elevations';
import { fontRegular, fontSemiBold } from '../../theme/fonts';
import { z10 } from '../../theme/z-index';
const THUMB_SIZE = size20;
const THUMB_MARGIN = `-${size8}`;
const createProgressGradient = (ratio, disabled, fillWithColorWhenDisabled, showHandle) => {
  if (disabled && !fillWithColorWhenDisabled) {
    return gray400;
  }

  // When the handle is hidden, we shouldn't take in consideration the size of the handle to calculate the filled area
  return `linear-gradient(${primary600}, ${primary600}) 0 / calc(0.5 * ${showHandle ? THUMB_SIZE : '0px'} + ${ratio} * (100% - ${showHandle ? THUMB_SIZE : '0px'})) 100% no-repeat, ${primary100}`;
};
const calculateToolTipOffset = ratio => {
  return `calc((0.5 * ${size20} + ${ratio} * (100% - ${size20})) - (${size20} / 2))`;
};
const Tooltip = styled.div`
  align-items: center;
  border-radius: ${size4};
  box-shadow: ${elevationThree};
  color: ${white};
  display: flex;
  font-family: ${fontSemiBold};
  font-size: ${size12};
  height: ${size24};
  justify-content: center;
  left: ${({
  ratio
}) => ratio && calculateToolTipOffset(ratio)};
  pointer-events: none;
  position: absolute;
  text-align: center;
  transition: opacity ${duration150} ${easeIn};

  ${({
  tooltipInThumb
}) => tooltipInThumb && css`
    border-radius: 50%;
    height: ${size20};
    top: -${size8};
    width: ${size20};
    z-index: ${z10};
  `}

  ${({
  tooltipInThumb
}) => !tooltipInThumb && css`
    top: -${size40};
    background: ${gray800};
    opacity: 0;
    padding: ${size4};
    min-width: ${size20};

    &::after {
      background: ${gray800};
      border-radius: 0 0 0 ${size2};
      bottom: -${size4};
      content: '';
      height: ${size8};
      position: absolute;
      transform: rotate(-45deg);
      width: ${size8};
    }
  `}
`;
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  margin-top: ${size16};
  border-top: ${size8} solid transparent;
  border-left: ${size4} solid transparent;
  border-right: ${size4} solid transparent;
  cursor: pointer;
  ${({
  disabled
}) => disabled && 'pointer-events: none;'}

  &:hover {
    ${Tooltip} { opacity: 1; }
  }
`;
const Input = styled.input`
  -webkit-appearance: none;
  background: transparent;
  cursor: pointer;
  margin: 0;
  position: relative;
  width: 100%;
  height: ${size4};

  &:focus {
    outline: none;

    &::-webkit-slider-thumb {
      box-shadow: 0 0 0 3px ${focusShadow};
    }

    &::-moz-range-thumb {
      box-shadow: 0 0 0 3px ${focusShadow};
    }

    &::-ms-thumb {
      box-shadow: 0 0 0 3px ${focusShadow};
    }
  }

  &::-webkit-slider-thumb {
    ${({
  showHandle
}) => !showHandle && 'display: none'};
    border-radius: ${size48};
    transition: box-shadow ${duration150} ${easeIn};
    box-shadow: ${elevationThree};
    border: 0;
    cursor: pointer;
    height: ${THUMB_SIZE};
    width: ${THUMB_SIZE};
    position: relative;
    z-index: ${z10};
    -webkit-appearance: none;
    margin-top: ${THUMB_MARGIN};
    background: ${({
  disabled,
  fillWithColorWhenDisabled
}) => disabled && !fillWithColorWhenDisabled ? gray400 : primary600};

    &:active {
      box-shadow: 0 0 0 3px ${focusShadow};
    }
  }

  &::-webkit-slider-runnable-track {
    border-radius: ${size32};
    cursor: pointer;
    height: ${size4};
    width: 100%;
    ${({
  $ratio,
  disabled,
  fillWithColorWhenDisabled,
  showHandle
}) => `background: ${createProgressGradient($ratio, disabled, fillWithColorWhenDisabled, showHandle)}`};
  }

  &::-moz-range-thumb {
    ${({
  showHandle
}) => !showHandle && 'display: none'};
    border-radius: ${size48};
    transition: box-shadow ${duration150} ${easeIn};
    box-shadow: ${elevationThree};
    border: 0;
    cursor: pointer;
    height: ${THUMB_SIZE};
    width: ${THUMB_SIZE};
    position: relative;
    z-index: ${z10};
    background: ${({
  disabled,
  fillWithColorWhenDisabled
}) => disabled && !fillWithColorWhenDisabled ? gray400 : primary600};

    &:active {
      box-shadow: 0 0 0 3px ${focusShadow};
    }
  }

  &::-moz-range-track {
    border-radius: ${size32};
    cursor: pointer;
    height: ${size4};
    width: 100%;
    ${({
  $ratio,
  disabled,
  fillWithColorWhenDisabled,
  showHandle
}) => `background: ${createProgressGradient($ratio, disabled, fillWithColorWhenDisabled, showHandle)}`};
  }

  &::-ms-fill-upper {
    background: transparent;
    border-color: transparent;
  }

  &::-ms-fill-lower {
    background: transparent;
    border-color: transparent;
  }

  &::-ms-thumb {
    ${({
  showHandle
}) => !showHandle && 'display: none'};
    border-radius: ${size48};
    transition: box-shadow ${duration150} ${easeIn};
    box-shadow: ${elevationThree};
    border: 0;
    cursor: pointer;
    height: ${THUMB_SIZE};
    width: ${THUMB_SIZE};
    position: relative;
    z-index: ${z10};
    background: ${({
  disabled,
  fillWithColorWhenDisabled
}) => disabled && !fillWithColorWhenDisabled ? gray400 : primary600};

    &:active {
      box-shadow: 0 0 0 3px ${focusShadow};
    }
  }

  &::-ms-track {
    border-radius: ${size32};
    cursor: pointer;
    height: ${size4};
    width: 100%;
    ${({
  $ratio,
  disabled,
  fillWithColorWhenDisabled,
  showHandle
}) => `background: ${createProgressGradient($ratio, disabled, fillWithColorWhenDisabled, showHandle)}`};
  }
`;
const FlexWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: ${size12};
`;
const LabelsWrapper = styled(FlexWrapper)`
  ${({
  justifyLabels
}) => justifyLabels && `justify-content: ${justifyLabels};`}
  ${({
  stepLabels
}) => !stepLabels && `margin-top: ${size16};`}
`;
const MarksWrapper = styled(FlexWrapper)`
  height: ${size4};
  left: 0;
  margin-top: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
`;
const Mark = styled.div`
  height: 100%;
  position: relative;
  width: ${size20};
  &::after {
    ${({
  $hidden
}) => $hidden && 'opacity: 0;'}
    background: ${gray500};
    border-radius: ${size32};
    content: '';
    display: block;
    height: ${size4};
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: ${size4};
  }
`;
const StepTooltip = styled.div`
  height: 100%;
  position: relative;
  width: ${size20};


  &::after {
    opacity: 50%;
    ${({
  $hidden
}) => $hidden && 'opacity: 0;'};
    background: ${primary600};
    border-radius: ${size32};
    content: '';
    display: block;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    height: ${THUMB_SIZE};
    width: ${THUMB_SIZE};
  }
`;
const Step = styled.span`
  color: ${({
  bold
}) => bold ? gray800 : gray600};
  font-weight: ${({
  bold
}) => bold ? '600' : 'normal'};
  font-size: ${size14};
  text-align: center;
  width: ${size20};
`;
const StepLabel = styled.span`
  font-family: ${fontRegular};
  color: ${gray800};
  font-size: ${size14};
  min-width: 0;
  overflow: hidden;
  position: relative;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: ${size2};
`;
export const Slider = /*#__PURE__*/forwardRef(({
  label,
  tooltip = true,
  min = 0,
  max = 10,
  value = 0,
  tooltipInThumb = false,
  labels = [],
  ariaValueText = "",
  justifyLabels = 'space-between',
  step = 1,
  onChange,
  stepLabels = false,
  disabled = false,
  marks,
  showHandle = true,
  fillWithColorWhenDisabled = false,
  empty = false,
  ...props
}, ref) => {
  const steps = Array.from({
    length: max - min + 1
  }, (_, i) => i + min);
  const ratio = (value - min) / (max - min);
  const showBarDot = index => !empty ? index <= value : false;
  const [stepTooltipIndex, setStepTooltipIndex] = useState();
  const wrapperRef = useRef(null);
  const showStepTooltip = index => index === stepTooltipIndex && (index !== value || empty);
  const isSelectedStep = index => !empty && (index === stepTooltipIndex || index === value);
  function onMouseMoveHandler(e) {
    const sliderWrapper = wrapperRef.current;
    if (sliderWrapper) {
      const sliderBoundingRect = sliderWrapper.getBoundingClientRect();
      const distanceFromStart = e.clientX - sliderBoundingRect.x;
      const percentage = distanceFromStart / sliderBoundingRect.width;
      const index = min + Math.round(percentage * (max - min) / step) * step;
      if (index < min || index > max) {
        return;
      }
      setStepTooltipIndex(index);
    }
  }
  function onClickHandler() {
    if (stepTooltipIndex !== undefined) onChange(stepTooltipIndex);
  }
  function onMouseLeave() {
    setStepTooltipIndex(undefined);
  }
  return /*#__PURE__*/React.createElement(Wrapper, {
    "data-testid": "wrapper",
    ref: wrapperRef,
    onClick: onClickHandler,
    onMouseMove: onMouseMoveHandler,
    onMouseLeave: onMouseLeave,
    disabled: disabled
  }, /*#__PURE__*/React.createElement(Input, _extends({
    role: "slider",
    type: "range",
    $ratio: ratio,
    value: value,
    min: min,
    max: max,
    step: step,
    disabled: disabled,
    "aria-label": label,
    "aria-valuetext": ariaValueText,
    "aria-describedby": "tooltip",
    onChange: e => onChange(parseInt(e.target.value)),
    ref: ref,
    fillWithColorWhenDisabled: fillWithColorWhenDisabled,
    showHandle: showHandle
  }, props)), marks && /*#__PURE__*/React.createElement(MarksWrapper, {
    "aria-hidden": true
  }, steps.map(s => /*#__PURE__*/React.createElement(Mark, {
    "data-testid": "mark",
    $hidden: showBarDot(s),
    key: s
  }))), tooltip && !empty && /*#__PURE__*/React.createElement(Tooltip, {
    tooltipInThumb: tooltipInThumb,
    ratio: ratio,
    id: "tooltip",
    role: "tooltip"
  }, value), /*#__PURE__*/React.createElement(MarksWrapper, {
    "aria-hidden": true
  }, steps.map(s => /*#__PURE__*/React.createElement(StepTooltip, {
    $hidden: !showStepTooltip(s),
    key: s
  }))), stepLabels && /*#__PURE__*/React.createElement(FlexWrapper, null, steps.map(s => /*#__PURE__*/React.createElement(Step, {
    bold: isSelectedStep(s),
    key: s
  }, s))), labels.length > 1 && /*#__PURE__*/React.createElement(LabelsWrapper, {
    justifyLabels,
    stepLabels
  }, labels.map((l, index) => /*#__PURE__*/React.createElement(StepLabel, {
    key: `label-${index}`
  }, l))));
});
Slider.displayName = 'Slider';
export default Slider;
//# sourceMappingURL=index.js.map